import {
  ALARM_REPEAT_COUNT,
  ALARM_RING_WINDOW_MS,
  DEFAULT_DUE_TIME,
  type AlarmItemKey,
  type ItemAlarm
} from "./alarmTypes";

// src/hrms/alarms/alarmSchedule.ts
/**
 * Pure scheduling rules, ported unchanged from the TeamLio mobile app so both
 * ring the same alarm at the same instants. Everything here is a function of
 * the stored alarms and "now": every open tab, after a reload, a sign-in or a
 * night with the browser closed, lands on the same occurrences and the same
 * notification ids. Deterministic ids are what keep two tabs (or a reload)
 * from ringing one alert twice.
 */

const MINUTE = 60_000;
/** Missed alarms older than this drop out of the list instead of piling up forever. */
export const MISSED_ALARM_HORIZON_MS = 7 * 24 * 60 * MINUTE;

/** One instant an alarm is meant to go off (an alert, a snooze or a "remind me again"). */
export type AlarmOccurrence = {
  at: number;
  id: string;
  reason: "alert" | "snooze" | "remind";
};

export type OccurrenceState = "upcoming" | "ringing" | "missed" | "handled";

/** One ring the engine must deliver: an occurrence's first ring or one of its repeats. */
export type PlannedAlert = {
  at: number;
  /** The last ring of the occurrence; after it the alarm counts as missed. */
  final: boolean;
  itemKey: AlarmItemKey;
  notificationId: string;
  occurrenceId: string;
  repeatIndex: number;
};

export type ActiveOccurrence = {
  alarm: ItemAlarm;
  itemKey: AlarmItemKey;
  occurrence: AlarmOccurrence;
};

const LOCAL_DATE_TIME = /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::(\d{2}))?)?$/;

/**
 * Epoch ms of a server date or date-time. The server stores naive local
 * timestamps, so a value with no zone is read as browser-local time; one with
 * a zone is honoured as given.
 */
export function parseAlarmDateTime(value: string | null | undefined): number | null {
  if (!value) return null;
  const trimmed = value.trim();
  const local = LOCAL_DATE_TIME.exec(trimmed.replace(/\.\d+$/, ""));
  if (local) {
    const [, year, month, day, hours, minutes, seconds] = local;
    const [defaultHours, defaultMinutes] = DEFAULT_DUE_TIME.split(":").map(Number);
    const date = new Date(
      Number(year),
      Number(month) - 1,
      Number(day),
      hours === undefined ? defaultHours : Number(hours),
      minutes === undefined ? defaultMinutes : Number(minutes),
      seconds === undefined ? 0 : Number(seconds)
    );
    return Number.isNaN(date.getTime()) ? null : date.getTime();
  }
  const parsed = Date.parse(trimmed);
  return Number.isNaN(parsed) ? null : parsed;
}

/** A local "YYYY-MM-DDTHH:mm" for a todo's due date plus its optional time of day. */
export function dueAtFromParts(dueDate: string | null | undefined, timeOfDay?: string | null): string | null {
  if (!dueDate) return null;
  const day = dueDate.slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(day)) return null;
  // A lifecycle due date can already carry a time ("2026-11-03T17:00:00").
  const embeddedTime = /T(\d{2}:\d{2})/.exec(dueDate)?.[1];
  const time = timeOfDay?.slice(0, 5) || embeddedTime || DEFAULT_DUE_TIME;
  return `${day}T${time}`;
}

export function localDateTimeString(epochMs: number) {
  const date = new Date(epochMs);
  const pad = (value: number) => String(value).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

export function isAlarmActive(alarm: ItemAlarm) {
  return alarm.enabled && !alarm.closed;
}

/**
 * Every instant this alarm is meant to go off, earliest first. Two alerts that
 * land on the same minute ring once.
 */
export function alarmOccurrences(alarm: ItemAlarm): AlarmOccurrence[] {
  const due = parseAlarmDateTime(alarm.item.dueAt);
  const occurrences: AlarmOccurrence[] = [];
  for (const alert of alarm.alerts) {
    const at = alert.kind === "offset"
      ? due === null ? null : due - alert.minutesBefore * MINUTE
      : parseAlarmDateTime(alert.at);
    if (at === null) continue;
    occurrences.push({ at, id: `${alert.id}@${at}`, reason: "alert" });
  }
  for (const followUp of alarm.followUps) {
    occurrences.push({ at: followUp.at, id: followUp.id, reason: followUp.reason });
  }
  occurrences.sort((left, right) => left.at - right.at);
  const seen = new Set<number>();
  return occurrences.filter((occurrence) => {
    const minute = Math.floor(occurrence.at / MINUTE);
    if (seen.has(minute)) return false;
    seen.add(minute);
    return true;
  });
}

function repeatIntervalMs(alarm: ItemAlarm) {
  return alarm.repeatEveryMinutes * MINUTE;
}

/** When each ring of an occurrence happens: the first, then every repeat interval. */
export function occurrenceAlertTimes(alarm: ItemAlarm, occurrence: AlarmOccurrence) {
  const interval = repeatIntervalMs(alarm);
  return Array.from({ length: ALARM_REPEAT_COUNT + 1 }, (_, index) => occurrence.at + index * interval);
}

/** How long one ring keeps sounding before it stops on its own. */
export function ringWindowMs(alarm: ItemAlarm) {
  return Math.min(repeatIntervalMs(alarm), ALARM_RING_WINDOW_MS);
}

/** The moment the last ring of an occurrence stops; after it the occurrence is missed. */
export function occurrenceEndsAt(alarm: ItemAlarm, occurrence: AlarmOccurrence) {
  const times = occurrenceAlertTimes(alarm, occurrence);
  return times[times.length - 1] + ringWindowMs(alarm);
}

export function occurrenceState(alarm: ItemAlarm, occurrence: AlarmOccurrence, now: number): OccurrenceState {
  if (alarm.handled[occurrence.id]) return "handled";
  if (occurrence.at > now) return "upcoming";
  return now < occurrenceEndsAt(alarm, occurrence) ? "ringing" : "missed";
}

/**
 * Whether an occurrence belongs to this alarm's live schedule. An alert that
 * was already in the past when the alarm was armed never counts; a follow-up
 * is always the user's own, recent request.
 */
function isLiveOccurrence(alarm: ItemAlarm, occurrence: AlarmOccurrence) {
  return occurrence.reason !== "alert" || occurrence.at >= alarm.armedAt - MINUTE;
}

export function alarmNotificationId(itemKey: AlarmItemKey, occurrenceId: string, repeatIndex: number) {
  return `alarm|${itemKey}|${occurrenceId}|${repeatIndex}`;
}

export function isAlarmNotificationId(id: string | undefined | null): boolean {
  return typeof id === "string" && id.startsWith("alarm|");
}

/** Every notification id an occurrence can produce, for cancelling it whole. */
export function occurrenceNotificationIds(itemKey: AlarmItemKey, occurrenceId: string) {
  return Array.from({ length: ALARM_REPEAT_COUNT + 1 }, (_, index) => alarmNotificationId(itemKey, occurrenceId, index));
}

/** The rings still to come, earliest first, capped at `budget`. */
export function planAlarmAlerts(alarms: Record<string, ItemAlarm>, now: number, budget: number): PlannedAlert[] {
  const planned: PlannedAlert[] = [];
  for (const [key, alarm] of Object.entries(alarms)) {
    if (!isAlarmActive(alarm)) continue;
    const itemKey = key as AlarmItemKey;
    for (const occurrence of alarmOccurrences(alarm)) {
      if (!isLiveOccurrence(alarm, occurrence) || alarm.handled[occurrence.id]) continue;
      const times = occurrenceAlertTimes(alarm, occurrence);
      times.forEach((at, repeatIndex) => {
        // A ring a second away is already due: it is the engine's to deliver now, not to wait for.
        if (at <= now + 1_000) return;
        planned.push({
          at,
          final: repeatIndex === times.length - 1,
          itemKey,
          notificationId: alarmNotificationId(itemKey, occurrence.id, repeatIndex),
          occurrenceId: occurrence.id,
          repeatIndex
        });
      });
    }
  }
  planned.sort((left, right) => left.at - right.at);
  return planned.slice(0, Math.max(0, budget));
}

function occurrencesInState(alarms: Record<string, ItemAlarm>, now: number, state: OccurrenceState): ActiveOccurrence[] {
  const matches: ActiveOccurrence[] = [];
  for (const [key, alarm] of Object.entries(alarms)) {
    if (!isAlarmActive(alarm)) continue;
    for (const occurrence of alarmOccurrences(alarm)) {
      if (!isLiveOccurrence(alarm, occurrence)) continue;
      if (occurrenceState(alarm, occurrence, now) !== state) continue;
      if (state === "missed" && now - occurrence.at > MISSED_ALARM_HORIZON_MS) continue;
      matches.push({ alarm, itemKey: key as AlarmItemKey, occurrence });
    }
  }
  return matches.sort((left, right) => left.occurrence.at - right.occurrence.at);
}

/** Alarms going off right now that nobody has answered, oldest first. */
export function ringingOccurrences(alarms: Record<string, ItemAlarm>, now: number) {
  return occurrencesInState(alarms, now, "ringing");
}

/** Alarms that rang out (or fell while the browser was closed) without an answer. */
export function missedOccurrences(alarms: Record<string, ItemAlarm>, now: number) {
  return occurrencesInState(alarms, now, "missed");
}

export function upcomingOccurrences(alarms: Record<string, ItemAlarm>, now: number) {
  return occurrencesInState(alarms, now, "upcoming");
}

export type AlarmBadgeState =
  | { kind: "off" }
  | { kind: "idle" }
  | { at: number; kind: "upcoming" }
  | { at: number; kind: "ringing" }
  | { at: number; count: number; kind: "missed" };

/** What an item's alarm icon should say: ringing beats missed beats upcoming. */
export function alarmBadgeState(alarm: ItemAlarm | undefined, now: number): AlarmBadgeState | null {
  if (!alarm || alarm.closed) return null;
  if (!alarm.enabled) return { kind: "off" };
  const single = { [alarmItemKeyOf(alarm)]: alarm };
  const ringing = ringingOccurrences(single, now);
  if (ringing.length) return { at: ringing[0].occurrence.at, kind: "ringing" };
  const missed = missedOccurrences(single, now);
  if (missed.length) return { at: missed[missed.length - 1].occurrence.at, count: missed.length, kind: "missed" };
  const upcoming = upcomingOccurrences(single, now);
  if (upcoming.length) return { at: upcoming[0].occurrence.at, kind: "upcoming" };
  return { kind: "idle" };
}

export function alarmItemKeyOf(alarm: ItemAlarm): AlarmItemKey {
  return `${alarm.item.source}:${alarm.item.id}`;
}

/** The ring of an occurrence that should be sounding right now. */
export type CurrentRing = {
  final: boolean;
  index: number;
  notificationId: string;
  /** When this ring stops sounding by itself (the next repeat, if any, starts later). */
  soundingUntil: number;
  startsAt: number;
};

/**
 * Which ring of a ringing occurrence is due at `now`, or null when none is
 * (not yet due, answered, between the end of one ring and the next repeat, or
 * rung out). A browser that slept through rings 0-3 delivers ring 4 only.
 */
export function currentRing(itemKey: AlarmItemKey, alarm: ItemAlarm, occurrence: AlarmOccurrence, now: number): CurrentRing | null {
  if (occurrenceState(alarm, occurrence, now) !== "ringing") return null;
  const interval = repeatIntervalMs(alarm);
  const index = Math.min(ALARM_REPEAT_COUNT, Math.floor((now - occurrence.at) / interval));
  const startsAt = occurrence.at + index * interval;
  const soundingUntil = startsAt + ringWindowMs(alarm);
  if (now >= soundingUntil) return null;
  return {
    final: index === ALARM_REPEAT_COUNT,
    index,
    notificationId: alarmNotificationId(itemKey, occurrence.id, index),
    soundingUntil,
    startsAt,
  };
}

/**
 * The next instant anything changes: a ring starts, a ring stops sounding, or
 * an occurrence rings out into "missed". The engine sleeps until then.
 */
export function nextAlarmWakeAt(alarms: Record<string, ItemAlarm>, now: number): number | null {
  let next: number | null = null;
  const consider = (at: number) => {
    if (at > now && (next === null || at < next)) next = at;
  };
  for (const { occurrence } of upcomingOccurrences(alarms, now)) consider(occurrence.at);
  for (const { alarm, itemKey, occurrence } of ringingOccurrences(alarms, now)) {
    occurrenceAlertTimes(alarm, occurrence).forEach(consider);
    const ring = currentRing(itemKey, alarm, occurrence, now);
    if (ring) consider(ring.soundingUntil);
    consider(occurrenceEndsAt(alarm, occurrence));
  }
  return next;
}
